import { User } from '../../../../data/models/user';
import { UserGroup } from '../../../../data/models/userGroup';

export interface EditInternalFormValues {
	// Basic Information - Name
	firstName: string;
	lastName: string;
	preferredName: string;
	preferredPronouns: string;

	// Basic Information - Contact Information
	email: string;
	personalEmail: string;
	phone: string;
	phoneCarrier: string;
	isIphone: string;
	smsTextReady: string;
	hasWhatsapp: string;
	whatsappNumber: string;
	hasWechat: string;
	wechatNumber: string;

	// Basic Information - Emergency Contact
	emergencyContactFirstName: string;
	emergencyContactLastName: string;
	emergencyContactPhone: string;

	// Basic Information - Home Address
	homeAddress: {
		line1: string;
		line2: string;
		city: string;
		state: string;
		zip: string;
	};

	// Basic Information - Shipping Address
	shippingIsSameAsHome: boolean;
	shippingAddress: {
		line1: string;
		line2: string;
		city: string;
		state: string;
		zip: string;
	};

	// Work & Personal Preferences - Work Details
	role: string;
	websiteUrl: string;
	instagramHandle: string;
	healthInsurance: string;
	professionalLicenses: string;

	// Work & Personal Preferences - Personal Preferences
	dietaryRestrictions: string;
	photographyConsent: string;
	shirtSize: string;
	pantSize: string;
	shoeSize: string;
	biopoc: string;

	// Travel Information - Airline Details
	homeAirport: string;
	preferredSeating: string;
	dateOfBirth: string;
	tsaPrecheck: string;
	globalEntry: string;
	passportNumber: string;
	passportIssuingCountry: string;
	passportExpirationDate: string;
	noPassport: boolean;
	preferredAirlines: string[]; // Changed from object to array to match internal form

	// Travel Information - Airline Numbers
	deltaMileage: string;
	unitedMileage: string;
	amtrackNumber: string;
	starwoodMember: string;
	emeraldClub: string;

	// Travel Information - Vehicle Information
	driversLicenseNumber: string;
	driversLicenseState: string;
	noDriversLicense: boolean;
	hasVehicle: string;
	willingToUseVehicleForProduction: string;
	vehicleType: string;
	vehicleLocation: string;

	// Additional fields for edit modal
	permissions: string;
	alwaysAddToProjects: boolean;
	groups: UserGroup[];
	rate: string;
	rateType: string;
}

export const createEditInternalDefaultValues = (): EditInternalFormValues => ({
	// Basic Information - Name
	firstName: '',
	lastName: '',
	preferredName: '',
	preferredPronouns: '',

	// Basic Information - Contact Information
	email: '',
	personalEmail: '',
	phone: '',
	phoneCarrier: '',
	isIphone: '',
	smsTextReady: '',
	hasWhatsapp: '',
	whatsappNumber: '',
	hasWechat: '',
	wechatNumber: '',

	// Basic Information - Emergency Contact
	emergencyContactFirstName: '',
	emergencyContactLastName: '',
	emergencyContactPhone: '',

	// Basic Information - Home Address
	homeAddress: {
		line1: '',
		line2: '',
		city: '',
		state: '',
		zip: '',
	},

	// Basic Information - Shipping Address
	shippingIsSameAsHome: false,
	shippingAddress: {
		line1: '',
		line2: '',
		city: '',
		state: '',
		zip: '',
	},

	// Work & Personal Preferences - Work Details
	role: '',
	websiteUrl: '',
	instagramHandle: '',
	healthInsurance: '',
	professionalLicenses: '',

	// Work & Personal Preferences - Personal Preferences
	dietaryRestrictions: '',
	photographyConsent: '',
	shirtSize: '',
	pantSize: '',
	shoeSize: '',
	biopoc: '',

	// Travel Information - Airline Details
	homeAirport: '',
	preferredSeating: '',
	dateOfBirth: '',
	tsaPrecheck: '',
	globalEntry: '',
	passportNumber: '',
	passportIssuingCountry: '',
	passportExpirationDate: '',
	noPassport: false,
	preferredAirlines: [], // Changed to array

	// Travel Information - Airline Numbers
	deltaMileage: '',
	unitedMileage: '',
	amtrackNumber: '',
	starwoodMember: '',
	emeraldClub: '',

	// Travel Information - Vehicle Information
	driversLicenseNumber: '',
	driversLicenseState: '',
	noDriversLicense: false,
	hasVehicle: '',
	willingToUseVehicleForProduction: '',
	vehicleType: '',
	vehicleLocation: '',

	// Additional fields for edit modal
	permissions: '',
	alwaysAddToProjects: false,
	groups: [],
	rate: '',
	rateType: 'HOURLY',
});

const sanitizeString = (value?: string): string | undefined => {
	if (typeof value !== 'string') {
		return undefined;
	}
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : undefined;
};

export const mapUserToEditInternalFormValues = (
	user: User
): EditInternalFormValues => {
	const defaultValues = createEditInternalDefaultValues();
	const userProfile: any = (user as any)?.userProfile || {};

	return {
		...defaultValues,

		// Basic Information - Name
		firstName: user.firstName || '',
		lastName: user.lastName || '',
		preferredName: user?.preferredName || '',
		preferredPronouns: user?.preferredPronouns || '',
		personalEmail: user?.personalEmail || '',

		// Basic Information - Contact Information
		email: user.email || '',
		phone: (user as any).phone || '',
		phoneCarrier: userProfile?.phoneCarrier || '',
		isIphone: userProfile?.isIphone
			? 'yes'
			: userProfile?.isIphone === false
			? 'no'
			: '',
		smsTextReady: userProfile?.smsTextReady
			? 'yes'
			: userProfile?.smsTextReady === false
			? 'no'
			: '',
		hasWhatsapp: userProfile?.hasWhatsapp
			? 'yes'
			: userProfile?.hasWhatsapp === false
			? 'no'
			: '',
		whatsappNumber: userProfile?.whatsappNumber || '',
		hasWechat: userProfile?.hasWechat
			? 'yes'
			: userProfile?.hasWechat === false
			? 'no'
			: '',
		wechatNumber: userProfile?.wechatNumber || '',

		// Basic Information - Emergency Contact
		emergencyContactFirstName: userProfile?.emergencyContactFirstName || '',
		emergencyContactLastName: userProfile?.emergencyContactLastName || '',
		emergencyContactPhone: userProfile?.emergencyContactPhone || '',

		// Basic Information - Home Address
		homeAddress: {
			line1: userProfile?.homeAddress?.line1 || '',
			line2: userProfile?.homeAddress?.line2 || '',
			city: userProfile?.homeAddress?.city || '',
			state: userProfile?.homeAddress?.state || '',
			zip: userProfile?.homeAddress?.zip || '',
		},

		// Basic Information - Shipping Address
		shippingIsSameAsHome:
			Boolean(userProfile?.shippingAddress?.sameAsHomeAddress) || false,
		shippingAddress: {
			line1: userProfile?.shippingAddress?.line1 || '',
			line2: userProfile?.shippingAddress?.line2 || '',
			city: userProfile?.shippingAddress?.city || '',
			state: userProfile?.shippingAddress?.state || '',
			zip: userProfile?.shippingAddress?.zip || '',
		},

		// Work & Personal Preferences - Work Details
		role: user.role || '',
		websiteUrl: userProfile?.websiteUrl || '',
		instagramHandle: userProfile?.instagramHandle || '',
		healthInsurance: userProfile?.healthInsurance
			? 'yes'
			: userProfile?.healthInsurance === false
			? 'no'
			: '',
		professionalLicenses: userProfile?.professionalLicenses || '',

		// Work & Personal Preferences - Personal Preferences
		dietaryRestrictions: userProfile?.dietaryRestrictions || '',
		photographyConsent: userProfile?.photographyConsent
			? 'yes'
			: userProfile?.photographyConsent === false
			? 'no'
			: '',
		shirtSize: userProfile?.shirtSize || '',
		pantSize: userProfile?.pantSize || '',
		shoeSize: userProfile?.shoeSize || '',
		biopoc: userProfile?.biopoc
			? 'yes'
			: userProfile?.biopoc === false
			? 'no'
			: '',

		// Travel Information - Airline Details
		homeAirport: userProfile?.homeAirport || '',
		preferredSeating: userProfile?.preferredSeating || '',
		dateOfBirth: userProfile?.dateOfBirth || '',
		tsaPrecheck: userProfile?.tsaPrecheck || '',
		globalEntry: userProfile?.globalEntry || '',
		passportNumber: userProfile?.passportNumber || '',
		passportIssuingCountry: userProfile?.passportIssuingCountry || '',
		passportExpirationDate: userProfile?.passportExpirationDate || '',
		noPassport: userProfile?.noPassport || false,
		preferredAirlines: userProfile?.preferredAirlines || [],

		// Travel Information - Airline Numbers
		deltaMileage: userProfile?.deltaMileage || '',
		unitedMileage: userProfile?.unitedMileage || '',
		amtrackNumber: userProfile?.amtrackNumber || '',
		starwoodMember: userProfile?.starwoodMember || '',
		emeraldClub: userProfile?.emeraldClub || '',

		// Travel Information - Vehicle Information
		driversLicenseNumber: userProfile?.driversLicenseNumber || '',
		driversLicenseState: userProfile?.driversLicenseState || '',
		noDriversLicense: userProfile?.noDriversLicense || false,
		hasVehicle: userProfile?.hasVehicle
			? 'yes'
			: userProfile?.hasVehicle === false
			? 'no'
			: '',
		willingToUseVehicleForProduction:
			userProfile?.willingToUseVehicleForProduction
				? 'yes'
				: userProfile?.willingToUseVehicleForProduction === false
				? 'no'
				: '',
		vehicleType: userProfile?.vehicleType || '',
		vehicleLocation: userProfile?.vehicleLocation || '',

		// Additional fields for edit modal
		permissions: user.permissions || '',
		alwaysAddToProjects: user.alwaysAddToProjects || false,
		groups: user.groups || [],
		rate: userProfile?.rate || '',
		rateType: userProfile?.rateType || 'HOURLY',
	};
};

export const buildEditInternalSavePayload = (
	values: EditInternalFormValues,
	isFieldDirty: (fieldPath: string) => boolean
) => {
	const updateData: any = {};

	// Basic Information - Name
	if (isFieldDirty('firstName'))
		updateData.firstName = sanitizeString(values.firstName);
	if (isFieldDirty('lastName'))
		updateData.lastName = sanitizeString(values.lastName);
	if (isFieldDirty('preferredName'))
		updateData.preferredName = sanitizeString(values.preferredName);
	if (isFieldDirty('preferredPronouns'))
		updateData.preferredPronouns = sanitizeString(values.preferredPronouns);

	// Basic Information - Contact Information
	if (isFieldDirty('email')) updateData.email = sanitizeString(values.email);
	if (isFieldDirty('personalEmail'))
		updateData.personalEmail = sanitizeString(values.personalEmail);
	if (isFieldDirty('phone')) updateData.phone = sanitizeString(values.phone);
	if (isFieldDirty('phoneCarrier'))
		updateData.phoneCarrier = sanitizeString(values.phoneCarrier);
	if (isFieldDirty('isIphone'))
		updateData.isIphone = values.isIphone === 'yes';
	if (isFieldDirty('smsTextReady'))
		updateData.smsTextReady = values.smsTextReady === 'yes';
	if (isFieldDirty('hasWhatsapp'))
		updateData.hasWhatsapp = values.hasWhatsapp === 'yes';
	if (isFieldDirty('whatsappNumber'))
		updateData.whatsappNumber = sanitizeString(values.whatsappNumber);
	if (isFieldDirty('hasWechat'))
		updateData.hasWechat = values.hasWechat === 'yes';
	if (isFieldDirty('wechatNumber'))
		updateData.wechatNumber = sanitizeString(values.wechatNumber);

	// Basic Information - Emergency Contact
	if (isFieldDirty('emergencyContactFirstName'))
		updateData.emergencyContactFirstName = sanitizeString(
			values.emergencyContactFirstName
		);
	if (isFieldDirty('emergencyContactLastName'))
		updateData.emergencyContactLastName = sanitizeString(
			values.emergencyContactLastName
		);
	if (isFieldDirty('emergencyContactPhone'))
		updateData.emergencyContactPhone = sanitizeString(
			values.emergencyContactPhone
		);

	// Basic Information - Home Address
	if (
		isFieldDirty('homeAddress.line1') ||
		isFieldDirty('homeAddress.line2') ||
		isFieldDirty('homeAddress.city') ||
		isFieldDirty('homeAddress.state') ||
		isFieldDirty('homeAddress.zip')
	) {
		updateData.homeAddress = {
			line1: sanitizeString(values.homeAddress?.line1),
			line2: sanitizeString(values.homeAddress?.line2),
			city: sanitizeString(values.homeAddress?.city),
			state: sanitizeString(values.homeAddress?.state),
			zip: sanitizeString(values.homeAddress?.zip),
		};
	}

	// Basic Information - Shipping Address
	if (isFieldDirty('shippingIsSameAsHome'))
		updateData.shippingIsSameAsHome = values.shippingIsSameAsHome;
	if (
		isFieldDirty('shippingAddress.line1') ||
		isFieldDirty('shippingAddress.line2') ||
		isFieldDirty('shippingAddress.city') ||
		isFieldDirty('shippingAddress.state') ||
		isFieldDirty('shippingAddress.zip')
	) {
		updateData.shippingAddress = {
			line1: sanitizeString(values.shippingAddress?.line1),
			line2: sanitizeString(values.shippingAddress?.line2),
			city: sanitizeString(values.shippingAddress?.city),
			state: sanitizeString(values.shippingAddress?.state),
			zip: sanitizeString(values.shippingAddress?.zip),
		};
	}

	// Work & Personal Preferences - Work Details
	if (isFieldDirty('role')) updateData.role = sanitizeString(values.role);
	if (isFieldDirty('websiteUrl'))
		updateData.websiteUrl = sanitizeString(values.websiteUrl);
	if (isFieldDirty('instagramHandle'))
		updateData.instagramHandle = sanitizeString(values.instagramHandle);
	if (isFieldDirty('healthInsurance'))
		updateData.healthInsurance = values.healthInsurance === 'yes';
	if (isFieldDirty('professionalLicenses'))
		updateData.professionalLicenses = sanitizeString(
			values.professionalLicenses
		);

	// Work & Personal Preferences - Personal Preferences
	if (isFieldDirty('dietaryRestrictions'))
		updateData.dietaryRestrictions = sanitizeString(
			values.dietaryRestrictions
		);
	if (isFieldDirty('photographyConsent'))
		updateData.photographyConsent = values.photographyConsent === 'yes';
	if (isFieldDirty('shirtSize'))
		updateData.shirtSize = sanitizeString(values.shirtSize);
	if (isFieldDirty('pantSize'))
		updateData.pantSize = sanitizeString(values.pantSize);
	if (isFieldDirty('shoeSize'))
		updateData.shoeSize = sanitizeString(values.shoeSize);
	if (isFieldDirty('biopoc')) updateData.biopoc = values.biopoc === 'yes';

	// Travel Information - Airline Details
	if (isFieldDirty('homeAirport'))
		updateData.homeAirport = sanitizeString(values.homeAirport);
	if (isFieldDirty('preferredSeating'))
		updateData.preferredSeating = sanitizeString(values.preferredSeating);
	if (isFieldDirty('dateOfBirth'))
		updateData.dateOfBirth = values.dateOfBirth || undefined;
	if (isFieldDirty('tsaPrecheck'))
		updateData.tsaPrecheck = sanitizeString(values.tsaPrecheck);
	if (isFieldDirty('globalEntry'))
		updateData.globalEntry = sanitizeString(values.globalEntry);
	if (isFieldDirty('passportNumber'))
		updateData.passportNumber = sanitizeString(values.passportNumber);
	if (isFieldDirty('passportIssuingCountry'))
		updateData.passportIssuingCountry = sanitizeString(
			values.passportIssuingCountry
		);
	if (isFieldDirty('passportExpirationDate'))
		updateData.passportExpirationDate =
			values.passportExpirationDate || undefined;
	if (isFieldDirty('noPassport')) updateData.noPassport = values.noPassport;
	if (isFieldDirty('preferredAirlines'))
		updateData.preferredAirlines = values.preferredAirlines;

	// Travel Information - Airline Numbers
	if (isFieldDirty('deltaMileage'))
		updateData.deltaMileage = sanitizeString(values.deltaMileage);
	if (isFieldDirty('unitedMileage'))
		updateData.unitedMileage = sanitizeString(values.unitedMileage);
	if (isFieldDirty('amtrackNumber'))
		updateData.amtrackNumber = sanitizeString(values.amtrackNumber);
	if (isFieldDirty('starwoodMember'))
		updateData.starwoodMember = sanitizeString(values.starwoodMember);
	if (isFieldDirty('emeraldClub'))
		updateData.emeraldClub = sanitizeString(values.emeraldClub);

	// Travel Information - Vehicle Information
	if (isFieldDirty('driversLicenseNumber'))
		updateData.driversLicenseNumber = sanitizeString(
			values.driversLicenseNumber
		);
	if (isFieldDirty('driversLicenseState'))
		updateData.driversLicenseState = sanitizeString(
			values.driversLicenseState
		);
	if (isFieldDirty('noDriversLicense'))
		updateData.noDriversLicense = values.noDriversLicense;
	if (isFieldDirty('hasVehicle'))
		updateData.hasVehicle = values.hasVehicle === 'yes';
	if (isFieldDirty('willingToUseVehicleForProduction'))
		updateData.willingToUseVehicleForProduction =
			values.willingToUseVehicleForProduction === 'yes';
	if (isFieldDirty('vehicleType'))
		updateData.vehicleType = sanitizeString(values.vehicleType);
	if (isFieldDirty('vehicleLocation'))
		updateData.vehicleLocation = sanitizeString(values.vehicleLocation);

	// Additional fields for edit modal
	if (isFieldDirty('permissions'))
		updateData.permissions = sanitizeString(values.permissions);
	if (isFieldDirty('alwaysAddToProjects'))
		updateData.alwaysAddToProjects = values.alwaysAddToProjects;
	if (isFieldDirty('groups'))
		updateData.groupIds = values.groups?.map((group) => group.id);
	if (isFieldDirty('rate')) updateData.rate = sanitizeString(values.rate);
	if (isFieldDirty('rateType'))
		updateData.rateType = sanitizeString(values.rateType);

	return updateData;
};
